'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

export type Language = 'fr' | 'en' | 'ar';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: 'fr',
  setLanguage: () => {},
});

export function useLanguage() {
  return useContext(LanguageContext);
}

function getCookieLanguage(): Language {
  if (typeof document === 'undefined') return 'fr';
  const match = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]+)/);
  const val = match?.[1];
  if (val === 'en' || val === 'ar' || val === 'fr') return val;
  return 'fr';
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => getCookieLanguage());

  useEffect(() => {
    // Sync in case cookie changed externally or SSR/client mismatch
    const cookieLang = getCookieLanguage();
    setLanguageState(cookieLang);
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    document.cookie = `NEXT_LOCALE=${lang}; path=/; max-age=31536000`;
    setLanguageState(lang);
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}
